import { PlusIcon, XIcon } from "lucide-react";

import { MAX_OPTIONS } from "./utils";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DecisionOption, DecisionQuestionType } from "@/types/prompt-blueprint";

interface DecisionOptionsEditorProps {
  type: DecisionQuestionType;
  options: DecisionOption[];
  onChange: (options: DecisionOption[]) => void;
}

export const DecisionOptionsEditor = ({ type, options, onChange }: DecisionOptionsEditorProps) => {
  const updateOption = (index: number, option: DecisionOption) =>
    onChange(options.map((current, i) => (i === index ? option : current)));

  const canAdd = options.length < MAX_OPTIONS[type];
  const canRemove = options.length > 2;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-gray-600">
        {type === "score" ? "Levels (lowest to highest)" : "Options"}
      </span>
      {options.map((option, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="flex items-center gap-2">
          <Input
            className="w-36 shrink-0"
            placeholder={type === "score" ? "Label" : "Value"}
            value={String(option.value)}
            onChange={e => updateOption(index, { ...option, value: e.target.value })}
          />
          <Input
            placeholder="Description (optional)"
            value={option.description ?? ""}
            onChange={e =>
              updateOption(index, { ...option, description: e.target.value || undefined })
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!canRemove}
            aria-label="Remove option"
            onClick={() => onChange(options.filter((_, i) => i !== index))}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {canAdd && (
        <Button
          type="button"
          variant="ghostBlue"
          size="sm"
          className="self-start"
          onClick={() => onChange([...options, { value: "" }])}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          {type === "score" ? "Add level" : "Add option"}
        </Button>
      )}
    </div>
  );
};
